import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import type { AccountInfo, EventMessage } from '@azure/msal-browser';
import { EventType } from '@azure/msal-browser';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { externalInstance, ubbLoginInstance } from './helper/authHelper';
import './i18n';
import * as Sentry from '@sentry/react';
import type { Query } from '@tanstack/react-query';
import {
  focusManager,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from './context/ThemeContext';

const globalQueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (
      error: Error,
      query: Query<unknown, unknown, unknown, readonly unknown[]>
    ) => {
      if (query.meta && typeof query.meta.onError === 'function') {
        query.meta.onError(error);
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      staleTime: 0,
    },
  },
});

if (process.env.REACT_APP_FETCH_DATA_ON_MOUSE_ENTER === 'true') {
  focusManager.setEventListener((onFocus: (focused?: boolean) => void) => {
    let wasBlurred = false;

    const handleFocusGain = () => {
      if (wasBlurred) {
        wasBlurred = false;
        onFocus();
      }
    };

    const handleWindowBlur = (e: FocusEvent | Event) => {
      if (e.target === window || e.target === document) {
        wasBlurred = true;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocusGain();
      } else {
        wasBlurred = true;
      }
    };

    const handleMouseLeave = () => {
      wasBlurred = true;
    };

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('blur', handleWindowBlur);
      window.addEventListener('focus', handleFocusGain);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      document.addEventListener('mousedown', handleFocusGain, true);
      document.addEventListener('mouseenter', handleFocusGain);
      document.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (typeof window !== 'undefined' && window.removeEventListener) {
        window.removeEventListener('blur', handleWindowBlur);
        window.removeEventListener('focus', handleFocusGain);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
        document.removeEventListener('mousedown', handleFocusGain, true);
        document.removeEventListener('mouseenter', handleFocusGain);
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  });
}

if (window.location.hostname !== 'localhost') {
  Sentry.init({
    dsn: 'https://c84e6dd50b5d6389440a6796d90bf388@o4508133068963840.ingest.de.sentry.io/4509559460069456',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
  });
}

// Default to using the first account if no account is active on page load
if (
  !ubbLoginInstance.getActiveAccount() &&
  ubbLoginInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  ubbLoginInstance.setActiveAccount(ubbLoginInstance.getAllAccounts()[0]);
}

ubbLoginInstance.addEventCallback((event: EventMessage) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    event.payload
  ) {
    ubbLoginInstance.setActiveAccount(event.payload as AccountInfo);
  }
});

// Default to using the first account if no account is active on page load
if (
  !externalInstance.getActiveAccount() &&
  externalInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  externalInstance.setActiveAccount(externalInstance.getAllAccounts()[0]);
}

externalInstance.addEventCallback((event: EventMessage) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    event.payload
  ) {
    externalInstance.setActiveAccount(event.payload as AccountInfo);
  }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <Suspense fallback={'Loading...'}>
      <QueryClientProvider client={globalQueryClient}>
        <ThemeProvider>
          <App />
          {process.env.REACT_APP_DEBUG === 'true' && (
            <ReactQueryDevtools initialIsOpen={true} />
          )}
        </ThemeProvider>
      </QueryClientProvider>
    </Suspense>
  </React.StrictMode>
);

reportWebVitals();
